import React, { useState } from 'react';
import { WebCryptoRamManager, DecryptedEnvelopeResult } from '../crypto/webcrypto-ram';

export interface AuditEnvelopeItem {
  eventId: string;
  userId: string;
  ciphertextBase64: string;
  ivBase64: string;
  tagBase64: string;
  wrappedKeyBase64?: string | null;
  aclMode: string;
  timestamp: number;
}

export interface DecryptedAuditViewerProps {
  cryptoManager: WebCryptoRamManager;
  envelopes?: AuditEnvelopeItem[];
}

export const DecryptedAuditViewer: React.FC<DecryptedAuditViewerProps> = ({
  cryptoManager,
  envelopes = [],
}) => {
  const [decryptedEvents, setDecryptedEvents] = useState<DecryptedEnvelopeResult[]>([]);
  const [isDecrypting, setIsDecrypting] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<DecryptedEnvelopeResult | null>(null);

  const decryptAllInRam = async () => {
    setIsDecrypting(true);
    const results: DecryptedEnvelopeResult[] = [];
    try {
      for (const env of envelopes) {
        try {
          const res = await cryptoManager.decryptEnvelope(env);
          results.push(res);
        } catch (err: any) {
          results.push({
            eventId: env.eventId,
            userId: env.userId,
            aclMode: env.aclMode,
            timestamp: env.timestamp,
            data: { error: `Decryption failed: ${err.message}` },
            integrityVerified: false,
          });
        }
      }
      setDecryptedEvents(results);
    } finally {
      setIsDecrypting(false);
    }
  };

  const getAclBadge = (mode: string) => {
    switch (mode) {
      case 'completely_isolated':
        return { label: '🔒 Completely Isolated', bg: '#F1F5F9', color: '#64748B' };
      case 'local_slm_summary_only':
        return { label: '🤖 Local SLM Summary Only', bg: '#FEF3C7', color: '#D97706' };
      case 'full_metadata':
      default:
        return { label: '📋 Full Metadata', bg: '#E0F2FE', color: '#0284C7' };
    }
  };

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
      border: '1px solid #E2E8F0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            backgroundColor: '#D8F3DC',
            color: '#1B4332',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
          }}>
            🛡️
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#2D3436', fontSize: '18px', fontWeight: 600 }}>
              Zero-Knowledge Decrypted Audit Stream
            </h3>
            <p style={{ margin: '2px 0 0', color: '#636E72', fontSize: '13px' }}>
              In-Memory RAM Decryption • Strict Privacy ACL Filtering • Media Telemetry Analytics
            </p>
          </div>
        </div>
        <button
          onClick={decryptAllInRam}
          disabled={isDecrypting}
          style={{
            backgroundColor: '#1B4332',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {isDecrypting ? 'Decrypting in RAM...' : '🔓 Decrypt Envelopes in RAM'}
        </button>
      </div>

      {decryptedEvents.length === 0 ? (
        <div style={{
          backgroundColor: '#F8FAFC',
          padding: '32px',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px dashed #CBD5E1',
          color: '#64748B',
          fontSize: '13px',
        }}>
          No envelopes decrypted yet. Tap "Decrypt Envelopes in RAM" to decrypt incoming settings events using your volatile private key.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px' }}>
          {/* Events List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {decryptedEvents.map((evt) => {
              const badge = getAclBadge(evt.aclMode);
              const isSelected = selectedEvent?.eventId === evt.eventId;
              const mediaTel =
                evt.data?.mediaPlaybackTelemetry ||
                (evt.data?.playedDurationSeconds !== undefined
                  ? {
                      mediaType: evt.data.mediaType || 'audio',
                      playedDurationSeconds: evt.data.playedDurationSeconds,
                      totalDurationSeconds: evt.data.totalDurationSeconds || evt.data.playedDurationSeconds,
                      completedFullListen: !!evt.data.completedFullListen,
                      volumeLevel: evt.data.volumeLevel ?? 1.0,
                      isMuted: !!evt.data.isMuted,
                      promptId: evt.data.promptId || 'prompt_default',
                      sessionId: evt.data.sessionId || 'sess_default',
                    }
                  : null);

              const percentListened = mediaTel
                ? Math.min(
                    100,
                    Math.round(
                      ((mediaTel.playedDurationSeconds || 0) /
                        (mediaTel.totalDurationSeconds || 1)) *
                        100
                    )
                  )
                : 0;

              return (
                <div
                  key={evt.eventId}
                  onClick={() => setSelectedEvent(evt)}
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    backgroundColor: isSelected ? '#EFF6FF' : '#F8FAFC',
                    border: `1px solid ${isSelected ? '#3B82F6' : '#E2E8F0'}`,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>
                      {evt.data?.action || (mediaTel ? 'media_playback_telemetry' : 'Settings Mutation')}
                    </span>
                    <span style={{
                      backgroundColor: badge.bg,
                      color: badge.color,
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '10px',
                      fontWeight: 700,
                    }}>
                      {badge.label}
                    </span>
                  </div>

                  {/* Media Telemetry Row in Event Card */}
                  {mediaTel && (
                    <div
                      style={{
                        marginTop: '8px',
                        padding: '8px 10px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '12px' }}>
                            {mediaTel.mediaType === 'video' ? '🎬' : '🎙️'}
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#334155' }}>
                            Therapist {mediaTel.mediaType === 'video' ? 'Video' : 'Audio'} Playback
                          </span>
                        </div>

                        {mediaTel.completedFullListen ? (
                          <span
                            style={{
                              backgroundColor: '#DCFCE7',
                              color: '#15803D',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}
                          >
                            ✓ 100% Full Listen
                          </span>
                        ) : (
                          <span
                            style={{
                              backgroundColor: '#FEF3C7',
                              color: '#B45309',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}
                          >
                            🎧 Partial ({percentListened}%)
                          </span>
                        )}
                      </div>

                      {/* Progress bar */}
                      <div style={{ width: '100%', height: '5px', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${percentListened}%`,
                            height: '100%',
                            backgroundColor: mediaTel.completedFullListen ? '#10B981' : '#F59E0B',
                            borderRadius: '3px',
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748B' }}>
                        <span>
                          {mediaTel.playedDurationSeconds}s / {mediaTel.totalDurationSeconds}s
                        </span>
                        <span>
                          {mediaTel.isMuted ? '🔇 Muted' : `🔊 Volume: ${Math.round((mediaTel.volumeLevel ?? 1) * 100)}%`}
                        </span>
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '6px' }}>
                    User: {evt.userId} • Event ID: {evt.eventId.substring(0, 12)}... • {new Date(evt.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Event Detail Payload Inspector & Media Card */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedEvent && (() => {
              const mediaTel =
                selectedEvent.data?.mediaPlaybackTelemetry ||
                (selectedEvent.data?.playedDurationSeconds !== undefined
                  ? {
                      mediaType: selectedEvent.data.mediaType || 'audio',
                      playedDurationSeconds: selectedEvent.data.playedDurationSeconds,
                      totalDurationSeconds: selectedEvent.data.totalDurationSeconds || selectedEvent.data.playedDurationSeconds,
                      completedFullListen: !!selectedEvent.data.completedFullListen,
                      volumeLevel: selectedEvent.data.volumeLevel ?? 1.0,
                      isMuted: !!selectedEvent.data.isMuted,
                      promptId: selectedEvent.data.promptId || 'prompt_default',
                      sessionId: selectedEvent.data.sessionId || 'sess_default',
                    }
                  : null);

              const percentListened = mediaTel
                ? Math.min(
                    100,
                    Math.round(
                      ((mediaTel.playedDurationSeconds || 0) /
                        (mediaTel.totalDurationSeconds || 1)) *
                        100
                    )
                  )
                : 0;

              return mediaTel ? (
                <div
                  style={{
                    backgroundColor: '#F8FAFC',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #CBD5E1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{mediaTel.mediaType === 'video' ? '🎬' : '🎙️'}</span>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#1E293B' }}>
                          Therapist Media Engagement Telemetry
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748B' }}>
                          Prompt: {mediaTel.promptId} • Session: {mediaTel.sessionId}
                        </div>
                      </div>
                    </div>

                    {mediaTel.completedFullListen ? (
                      <span
                        style={{
                          backgroundColor: '#DCFCE7',
                          color: '#15803D',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 700,
                          border: '1px solid #86EFAC',
                        }}
                      >
                        ✓ Completed Full Listen
                      </span>
                    ) : (
                      <span
                        style={{
                          backgroundColor: '#FEF3C7',
                          color: '#B45309',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 700,
                          border: '1px solid #FDE68A',
                        }}
                      >
                        🎧 Partial Playback ({percentListened}%)
                      </span>
                    )}
                  </div>

                  {/* Large Duration Progress Bar */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                      <span>Playback Duration: {mediaTel.playedDurationSeconds}s / {mediaTel.totalDurationSeconds}s</span>
                      <span>{percentListened}%</span>
                    </div>
                    <div style={{ width: '100%', height: '8px', backgroundColor: '#E2E8F0', borderRadius: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${percentListened}%`,
                          height: '100%',
                          backgroundColor: mediaTel.completedFullListen ? '#10B981' : '#3B82F6',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>

                  {/* Volume & Audio Output State */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                      backgroundColor: '#FFFFFF',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '11px',
                      color: '#475569',
                    }}
                  >
                    <div>
                      <strong>Volume Level:</strong> {Math.round((mediaTel.volumeLevel ?? 1) * 100)}%
                    </div>
                    <div>•</div>
                    <div>
                      <strong>Muted State:</strong>{' '}
                      {mediaTel.isMuted ? (
                        <span style={{ color: '#DC2626', fontWeight: 700 }}>🔇 Audio Muted (Captions Active)</span>
                      ) : (
                        <span style={{ color: '#16A34A', fontWeight: 700 }}>🔊 Unmuted (Audio Active)</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* In-RAM Raw Plaintext Inspection */}
            <div style={{
              backgroundColor: '#0F172A',
              borderRadius: '12px',
              padding: '16px',
              color: '#E2E8F0',
              fontFamily: 'monospace',
              fontSize: '11px',
              maxHeight: '280px',
              overflowY: 'auto',
            }}>
              <div style={{ color: '#10B981', fontWeight: 'bold', marginBottom: '8px' }}>
                // In-Memory Plaintext Inspection
              </div>
              {selectedEvent ? (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedEvent, null, 2)}
                </pre>
              ) : (
                <div style={{ color: '#64748B', fontStyle: 'italic' }}>
                  Select an event from the list to view the in-RAM decrypted JSON structure.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DecryptedAuditViewer;
